'use client'

import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { getPremiumStatusAction } from './UserPremiumActions'
import UserPremiumFeatureComponent from './UserPremiumFeatureComponent'
import UserPremiumStatusComponent from './UserPremiumStatusComponent'
import { Button } from 'components/ui/button'
import { IconCustomGoldPremium } from 'components/icons'
import LoadingAnimation from 'components/layout/LoadingAnimation'

dayjs.extend(utc)
dayjs.extend(timezone)

interface PremiumComponentProps {
    texts: {
        userPremiumTitle: string
        getPremium: string
        cancelPremium: string
        userPremiumDescription: string
        userPremiumDescriptionRenew: string
        userPremiumPriceText: string
        userPremiumUnsubscribeConfirmationTitle: string
        areYouSureYouWantToCancelYourPremiumSubscriptionYouWillLoseAccessToAllPremiumFeaturesAtTheEndOfYourCurrentSubscriptionPeriod: string
        keepPremium: string
        userPremiumUnsubscribeConfirmationConfirm: string
        premiumSubscriptionCancelledSuccessfully: string
        userPremiumStatusNotAvailableTitle: string
        userPremiumStatusNotAvailableText: string
        userPremiumRetryLoadingPremiumStatus: string
        userPremiumStatusNotActive: string
        userPremiumStatusActive: string
        userPremiumStatusExpiring: string
        userPremiumStatusCanceled: string
        userPremiumStatusExpired: string
        expiryMessageInactive: string
        subscriptionMessageInactive: string
        expiryMessageActive: string
        subscriptionMessageActive: string
        expiryMessageExpiring: string
        subscriptionMessageExpiring: string
        expiryMessageCanceling: string
        subscriptionMessageCanceling: string
        expiryMessageCanceled: string
        subscriptionMessageCanceled: string
        expiryMessageExpired: string
        subscriptionMessageExpired: string
        buttonTextUpdatePayment: string
        buttonTextRenewSubscription: string
        paymentProviderRedirect: string
        errorLoadingUserPremiumStatus: string
        errorResumingSubscription: string
        errorSubscribing: string
        errorUnsubscribing: string
        features: IPremiumFeatures[]
        notLoggedIn: string
    }
    user?: IUser | undefined
    lang: string
}

/** Player premium only. Organisation premium is in Admin → Organisation Premium. */
const UserPremiumComponent = ({ texts, user, lang }: PremiumComponentProps) => {
    const [mounted, setMounted] = useState(false)
    const [premiumStatus, setPremiumStatus] = useState<IPremiumStatus | null>(null)
    const [isLoading, setIsLoading] = useState(true) // Start with true to show loading on initial mount
    const [statusError, setStatusError] = useState<string | null>(null)
    const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)

    const getFeaturesAndPricing = () => {
        const freeFeatures = texts.features
            .filter(f => f.subscriber === 'user' && f.type === 'free')
            .flatMap(f => f.features)
        const premiumFeatures = texts.features
            .filter(f => f.subscriber === 'user' && f.type === 'premium')
            .flatMap(f => f.features)
        const premiumPrice = texts.features.find(f => f.subscriber === 'user' && f.type === 'premium')?.price || ''
        return { freeFeatures, premiumFeatures, premiumPrice }
    }

    useEffect(() => {
        setMounted(true)

        // Always fetch premium status when user is logged in (not just when user.premium is true)
        // This ensures we get the actual status from the database
        if (user) {
            fetchPremiumStatus(true)
        } else {
            // If no user, we're done loading
            setIsLoading(false)
            setHasInitiallyLoaded(true)
        }

        const handlePremiumStatusUpdated = () => {
            fetchPremiumStatus(true)
        }

        const checkForSuccessfulPayment = () => {
            if (typeof window !== 'undefined') {
                const urlParams = new URLSearchParams(window.location.search)
                const result = urlParams.get('result')
                const sessionId = urlParams.get('session_id')

                if (result === 'success' && sessionId) {
                    const url = new URL(window.location.href)
                    url.searchParams.delete('result')
                    url.searchParams.delete('session_id')
                    window.history.replaceState({}, '', url)

                    fetchPremiumStatus(true)
                    toast.success('Payment successful! Your premium subscription is now active.')
                }
            }
        }

        checkForSuccessfulPayment()

        window.addEventListener('premiumStatusUpdated', handlePremiumStatusUpdated)

        return () => {
            window.removeEventListener('premiumStatusUpdated', handlePremiumStatusUpdated)
        }
    }, [user])

    const fetchPremiumStatus = async (force = false) => {
        if (!user) {
            setIsLoading(false)
            setHasInitiallyLoaded(true)
            return
        }
        if (!force && premiumStatus && hasInitiallyLoaded) return

        try {
            setIsLoading(true)
            const result = await getPremiumStatusAction()

            if ('error' in result) {
                setStatusError(result.error as string)
                setPremiumStatus(null)
            } else {
                setPremiumStatus(result as IPremiumStatus)
                setStatusError(null)
            }
        } catch (error) {
            setStatusError('Failed to load subscription status')
            setPremiumStatus(null)
        } finally {
            setIsLoading(false)
            setHasInitiallyLoaded(true)
        }
    }

    const renderPricingCards = () => {
        const { freeFeatures, premiumFeatures, premiumPrice } = getFeaturesAndPricing()
        return (
            <div className='grid md:grid-cols-2 gap-4 items-center'>
                <div className='w-full py-10 mx-auto shadow-xl border p-5 md:p-6 rounded-xl gap-y-5 grid glassmorphism h-fit'>
                    <div className='flex justify-between items-center gap-2'>
                        <h4 className='text-xl md:text-2xl font-bold flex items-center gap-2'>
                            <span>Free</span>
                        </h4>
                    </div>
                    <hr />
                    <div>
                        <UserPremiumFeatureComponent features={freeFeatures} />
                        <Button variant='outline' disabled={true} className='mt-6 w-full h-16'>
                            Free
                        </Button>
                    </div>
                </div>

                <div className='w-full max-w-[550px] h-fit py-10 mx-auto shadow-xl border p-5 md:p-6 rounded-xl gap-y-5 grid glassmorphism'>
                    <div className='flex justify-between items-center gap-2'>
                        <h4 className='text-xl md:text-2xl font-bold flex items-center gap-2'>
                            <span>{texts.userPremiumTitle}</span>
                            <IconCustomGoldPremium className='size-6' />
                        </h4>
                        <div className='flex flex-row items-center justify-center '>
                            <p className='text-xl md:text-2xl font-bold'>€{premiumPrice}</p>
                            <span className='lowercase text-xs -mb-1.5 flex ml-1'>/ {texts.userPremiumPriceText}</span>
                        </div>
                    </div>
                    <hr />
                    <div>
                        <UserPremiumFeatureComponent features={premiumFeatures} />
                        <UserPremiumStatusComponent
                            premiumStatus={premiumStatus}
                            isLoading={isLoading}
                            statusError={statusError}
                            fetchPremiumStatus={fetchPremiumStatus}
                            texts={texts}
                            lang={lang}
                        />
                    </div>
                </div>
            </div>
        )
    }

    let premiumDescription

    if (premiumStatus === null) premiumDescription = texts.userPremiumDescription
    else
        premiumDescription =
            !premiumStatus?.status || premiumStatus?.status === 'inactive'
                ? texts.userPremiumDescription
                : texts.userPremiumDescriptionRenew

    if (!mounted) return null

    // Show loading state without any other content when initially loading
    // This prevents showing misleading information while fetching status
    if (isLoading && !hasInitiallyLoaded) {
        return (
            <div className='flex-1 gap-y-4 overflow-auto rounded-xl content-center'>
                <div className='grid h-fit inset-y-0 my-auto'>
                    <div className='flex flex-col gap-2 flex-1'>
                        <div className='w-full py-10 mx-auto shadow-xl lg:min-h-[700px] border p-4 rounded-xl gap-y-5 grid glassmorphism h-full'>
                            <div className='flex items-center justify-center min-h-[400px]'>
                                <LoadingAnimation />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className='flex-1 gap-y-4 overflow-auto rounded-xl content-center'>
            <div className='grid h-fit inset-y-0 my-auto'>
                <div className='flex flex-col gap-2 flex-1'>
                    <div className='w-full py-10 mx-auto shadow-xl lg:min-h-[700px] border p-4 rounded-xl gap-y-5 grid glassmorphism h-full'>
                        <p className='text-gray-200 max-w-[600px] mx-auto text-center md:text-xl lg:tex-2xl font-bold mb-6 '>
                            {premiumDescription}
                        </p>
                        <hr className='mb-4' />
                        {premiumStatus === null ? (
                            renderPricingCards()
                        ) : (
                            <div className='w-full max-w-[550px] py-10 mx-auto shadow-xl border p-5 md:p-6 rounded-xl gap-y-5 grid glassmorphism'>
                                <UserPremiumStatusComponent
                                    premiumStatus={premiumStatus}
                                    isLoading={isLoading}
                                    statusError={statusError}
                                    fetchPremiumStatus={fetchPremiumStatus}
                                    texts={texts}
                                    lang={lang}
                                />
                                {premiumStatus?.status !== 'active' && (
                                    <UserPremiumFeatureComponent features={getFeaturesAndPricing().freeFeatures} />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default UserPremiumComponent
